import React, { useState, useEffect, useRef } from 'react';
import { api } from '@/api/apiClient';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MessageCircle, X, Send, Minimize2, Maximize2, Loader2, Mic, MicOff, Phone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { usePageContext, generateAIContextMessage } from '@/hooks/usePageContext';
import ConfirmDialog from '@/components/ConfirmDialog';
import useConfirmDialog from '@/hooks/useConfirmDialog';

// Session storage keys
const STORAGE_KEYS = {
  CONVERSATION_ID: 'ai_chat_conversation_id',
  IS_CALL_ACTIVE: 'ai_chat_call_active',
  LAST_PAGE: 'ai_chat_last_page',
  SESSION_ACTIVE: 'ai_chat_session_active'
};

export default function EnrollmentAIChat({ context = {} }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [aiConfig, setAiConfig] = useState({ provider: 'host' });
  const [vapiClient, setVapiClient] = useState(null);
  const [isCallActive, setIsCallActive] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const messagesEndRef = useRef(null);
  const lastPageRef = useRef(null);
  const { confirmDialogProps, openConfirm } = useConfirmDialog();
  
  // Track current page context with user role
  const pageContext = usePageContext(currentUser);

  // Load AI config and restore session on mount
  useEffect(() => {
    loadCurrentUser();
    loadAIConfig();
    restoreSession();
  }, []);
  
  const loadCurrentUser = async () => {
    try {
      const user = await api.auth.me();
      setCurrentUser(user);
    } catch (err) {
      console.error('Failed to load current user:', err);
    }
  };

  // Initialize conversation when opened
  useEffect(() => {
    if (isOpen && !conversationId) {
      initConversation();
    }
    
    // Send initial context to VAPI when chat is opened
    if (isOpen && aiConfig.provider === 'vapi' && vapiClient && pageContext) {
      const contextMessage = generateAIContextMessage(pageContext, {
        itemCount: context.cartItems?.length || 0,
        selectedCategory: context.selectedCategory,
        enrollmentStatus: context.enrollmentStatus
      });
      
      vapiClient.send({
        type: 'add-message',
        message: {
          role: 'system',
          content: `[Initial Context] ${contextMessage}\n\nThe user has opened the chat widget. Be ready to assist them.`
        }
      });
      
      console.log('Sent initial context to VAPI on open:', contextMessage);
    }
  }, [isOpen, aiConfig, vapiClient, pageContext]);

  // Subscribe to conversation updates
  useEffect(() => {
    if (conversationId) {
      const unsubscribe = api.agents.subscribeToConversation(conversationId, (data) => {
        setMessages(data.messages || []);
        scrollToBottom();
      });
      return () => unsubscribe();
    }
  }, [conversationId]);

  // Track page changes and send context updates
  useEffect(() => {
    if (pageContext) {
      const hasActiveSession = localStorage.getItem(STORAGE_KEYS.SESSION_ACTIVE) === 'true';
      const lastPage = lastPageRef.current;
      
      // Send context update if:
      // 1. Session is active AND page changed, OR
      // 2. Using VAPI and chat is open (VAPI doesn't need conversation ID)
      const shouldSendUpdate = (hasActiveSession && lastPage && lastPage !== pageContext.pageName) ||
                               (aiConfig.provider === 'vapi' && isOpen && vapiClient && lastPage && lastPage !== pageContext.pageName);
      
      if (shouldSendUpdate) {
        sendPageContextUpdate(pageContext);
      }
      
      lastPageRef.current = pageContext.pageName;
      localStorage.setItem(STORAGE_KEYS.LAST_PAGE, pageContext.pageName);
    }
  }, [pageContext, conversationId, isOpen, vapiClient, aiConfig.provider]);

  // Update session state when conversation or call state changes
  useEffect(() => {
    if (conversationId) {
      localStorage.setItem(STORAGE_KEYS.CONVERSATION_ID, conversationId);
      localStorage.setItem(STORAGE_KEYS.SESSION_ACTIVE, 'true');
    }
  }, [conversationId]);

  useEffect(() => {
    if (isCallActive !== undefined) {
      localStorage.setItem(STORAGE_KEYS.IS_CALL_ACTIVE, isCallActive.toString());
    }
  }, [isCallActive]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const restoreSession = () => {
    try {
      const storedConversationId = localStorage.getItem(STORAGE_KEYS.CONVERSATION_ID);
      const isSessionActive = localStorage.getItem(STORAGE_KEYS.SESSION_ACTIVE) === 'true';
      const wasCallActive = localStorage.getItem(STORAGE_KEYS.IS_CALL_ACTIVE) === 'true';
      const lastPage = localStorage.getItem(STORAGE_KEYS.LAST_PAGE);
      
      if (isSessionActive && storedConversationId) {
        setConversationId(storedConversationId);
        setIsCallActive(wasCallActive);
        lastPageRef.current = lastPage;
        
        // Restore conversation messages
        api.agents.getConversation(storedConversationId)
          .then(conv => {
            if (conv && conv.messages) {
              setMessages(conv.messages);
            }
          })
          .catch(err => {
            console.warn('Could not restore conversation:', err);
            clearSession();
          });
      }
    } catch (err) {
      console.error('Failed to restore session:', err);
    }
  };

  const clearSession = () => {
    localStorage.removeItem(STORAGE_KEYS.CONVERSATION_ID);
    localStorage.removeItem(STORAGE_KEYS.IS_CALL_ACTIVE);
    localStorage.removeItem(STORAGE_KEYS.LAST_PAGE);
    localStorage.removeItem(STORAGE_KEYS.SESSION_ACTIVE);
    setConversationId(null);
    setMessages([]);
    setIsCallActive(false);
  };

  const sendPageContextUpdate = async (pageContext) => {
    try {
      // Generate a context message without sensitive data
      const contextMessage = generateAIContextMessage(pageContext, {
        itemCount: context.cartItems?.length || 0,
        selectedCategory: context.selectedCategory,
        enrollmentStatus: context.enrollmentStatus
      });

      // Send to local storage for mock AI (if conversation exists)
      if (conversationId) {
        await api.agents.sendInbandMessage(conversationId, {
          type: 'page_change',
          pageName: pageContext.pageName,
          pageTitle: pageContext.title,
          message: contextMessage,
          capabilities: pageContext.capabilities,
          helpTopics: pageContext.helpTopics,
          timestamp: new Date().toISOString()
        });
      }

      // If using VAPI, send context update to VAPI as well
      if (aiConfig.provider === 'vapi' && vapiClient) {
        vapiClient.send({
          type: 'add-message',
          message: {
            role: 'system',
            content: `[Page Context Update] ${contextMessage}\n\nYou can now assist the user with questions about this page.`
          }
        });
        console.log('Sent page context to VAPI:', pageContext.pageName, '-', contextMessage);
      } else {
        console.log('Sent page context update to local storage:', pageContext.pageName);
      }
    } catch (err) {
      console.error('Failed to send page context update:', err);
    }
  };

  const loadAIConfig = async () => {
    try {
      const configs = await api.entities.SystemConfig.filter({
        config_key: { in: ['ai_provider', 'vapi_public_key', 'vapi_agent_id'] }
      });
      
      const configMap = {};
      configs.forEach(c => {
        configMap[c.config_key] = c.value;
      });

      const provider = configMap.ai_provider || 'host';
      setAiConfig({
        provider,
        vapiPublicKey: configMap.vapi_public_key,
        vapiAgentId: configMap.vapi_agent_id
      });

      if (provider === 'vapi' && configMap.vapi_public_key) {
        initializeVAPI(configMap.vapi_public_key, configMap.vapi_agent_id);
      }
    } catch (err) {
      console.error('Failed to load AI config:', err);
    }
  };

  const initializeVAPI = async (publicKey, agentId) => {
    try {
      const { default: Vapi } = await import('https://cdn.jsdelivr.net/npm/@vapi-ai/web@2.5.0/dist/index.js');
      
      const client = new Vapi(publicKey);
      
      client.on('message', (message) => {
        if (message.type === 'transcript' && message.role === 'assistant' && message.transcript) {
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: message.transcript
          }]);
        } else if (message.type === 'transcript' && message.role === 'user' && message.transcript) {
          setMessages(prev => [...prev, {
            role: 'user',
            content: message.transcript
          }]);
        }
      });

      client.on('call-start', () => {
        setIsCallActive(true);
      });

      client.on('call-end', () => {
        setIsCallActive(false);
        setIsMuted(false);
      });

      setVapiClient(client);
    } catch (err) {
      console.error('Failed to initialize VAPI:', err);
    }
  };

  const initConversation = async () => {
    try {
      const user = await api.auth.me();
      
      // Include page context in initial setup
      const initialContext = pageContext ? generateAIContextMessage(pageContext, {
        itemCount: context.cartItems?.length || 0,
        selectedCategory: context.selectedCategory
      }) : '';

      const contextInfo = {
        page: pageContext?.pageName || context.currentPage || 'enrollment',
        pageTitle: pageContext?.title || 'Enrollment',
        pageDescription: pageContext?.description || '',
        capabilities: pageContext?.capabilities || [],
        helpTopics: pageContext?.helpTopics || [],
        initialContext,
        selectedBenefits: context.selectedBenefits || [],
        employeeData: context.employeeData || {},
        cartItems: context.cartItems || []
      };

      const conversation = await api.agents.createConversation({
        agent_name: 'victoria',
        metadata: {
          name: `Enrollment Assistance - ${user.full_name}`,
          context: contextInfo
        }
      });
      
      setConversationId(conversation.id);
      
      // Track the initial page
      if (pageContext) {
        lastPageRef.current = pageContext.pageName;
        localStorage.setItem(STORAGE_KEYS.LAST_PAGE, pageContext.pageName);
      }
    } catch (err) {
      console.error('Failed to init conversation:', err);
    }
  };

  const generateMockAIResponse = (userMessage, conversation) => {
    // Get user role for role-specific responses
    const userRole = currentUser?.user_role || currentUser?.role || 'employee';
    const isAdmin = userRole === 'admin' || userRole === 'sub_admin';
    const isHR = userRole === 'employer_hr';
    const isEmployee = userRole === 'employee';
    
    // Simple mock AI that uses page context to respond
    const lowerMessage = userMessage.toLowerCase();
    
    // Extract current page context from conversation metadata or recent system messages
    let currentPageTitle = pageContext?.title || 'Unknown';
    let currentPageDescription = pageContext?.description || '';
    let currentCapabilities = pageContext?.capabilities || [];
    
    // Check for page context in recent system messages
    const systemMessages = conversation.messages.filter(m => m.role === 'system');
    if (systemMessages.length > 0) {
      const lastSystemMsg = systemMessages[systemMessages.length - 1];
      if (lastSystemMsg.metadata?.pageTitle) {
        currentPageTitle = lastSystemMsg.metadata.pageTitle;
        currentPageDescription = lastSystemMsg.metadata.message || '';
      }
    }
    
    // Role-based identity and scope
    let aiIdentity = '';
    let aiScope = '';
    
    if (isAdmin) {
      aiIdentity = "I'm Victoria, your system administration assistant!";
      aiScope = "I can help you manage employers, configure benefits, review system settings, and assist with administrative tasks.";
    } else if (isHR) {
      aiIdentity = "I'm Victoria, your HR management assistant!";
      aiScope = "I can help you manage employees, configure company benefits, review enrollment data, and assist with HR administrative tasks.";
    } else {
      aiIdentity = "I'm Victoria, your benefits assistant!";
      aiScope = "I can help you understand your benefits, answer questions about enrollment, explain coverage options, and guide you through the process.";
    }
    
    // Context-aware responses
    if (lowerMessage.includes('what page') || lowerMessage.includes('where am i') || lowerMessage.includes('current page')) {
      return `You're currently on the **${currentPageTitle}** page. ${currentPageDescription}\n\n${currentCapabilities.length > 0 ? `Here's what you can do on this page:\n${currentCapabilities.map(c => `- ${c}`).join('\n')}` : ''}\n\nHow can I help you with this?`;
    }
    
    if (lowerMessage.includes('help') || lowerMessage.includes('what can you do')) {
      return `${aiIdentity} I can see you're on the **${currentPageTitle}** page.\n\n${currentCapabilities.length > 0 ? `On this page, you can:\n${currentCapabilities.map(c => `- ${c}`).join('\n')}\n\n` : ''}${aiScope} What would you like to know?`;
    }
    
    // Admin-specific responses
    if (isAdmin) {
      if (lowerMessage.includes('employer') && currentPageTitle.includes('Employer')) {
        return `I can see you're managing employers. You can view all employer accounts, create new employers, configure their benefit offerings, and manage their settings. What would you like to do?`;
      }
      if (lowerMessage.includes('benefit') && (currentPageTitle.includes('Benefits Management') || currentPageTitle.includes('Categories'))) {
        return `You're working with the benefits catalog. You can create new benefit products, edit existing ones, set pricing and rating tables, and organize products by category. What aspect of benefit management can I help with?`;
      }
      if (lowerMessage.includes('employee') && currentPageTitle.includes('Employee Management')) {
        return `You have access to system-wide employee management. You can view all employees across all employers, manage their accounts, and perform bulk operations. What do you need help with?`;
      }
      if (lowerMessage.includes('report') || lowerMessage.includes('analytics')) {
        return `You have access to comprehensive system analytics and reports including enrollment data, demographic information, tax savings calculations, and penetration metrics across all employers. What insights are you looking for?`;
      }
    }
    
    // HR-specific responses
    if (isHR) {
      if (lowerMessage.includes('employee') && (currentPageTitle.includes('Employees') || currentPageTitle.includes('Employee Management'))) {
        return `You're managing your company's employees. You can add new employees, edit their information, manage enrollment status, and assist them with benefit questions. What would you like to do?`;
      }
      if (lowerMessage.includes('enrollment') && currentPageTitle.includes('Enrollment')) {
        return `You can monitor your employees' enrollment status, view who has completed enrollment, send reminders, and assist employees who need help. You can also configure enrollment periods and settings for your company.`;
      }
      if (lowerMessage.includes('report') || lowerMessage.includes('analytics')) {
        return `You have access to your company's reports including enrollment data, demographic breakdowns, tax savings, and benefit penetration rates. These help you understand your employees' benefit selections and overall participation. What data would you like to review?`;
      }
      if (lowerMessage.includes('invite') || lowerMessage.includes('onboard')) {
        return `You can invite employees to enroll by generating invitation codes, sending email invites, and tracking who has joined the system. This helps onboard new hires efficiently. Would you like help with employee invitations?`;
      }
    }
    
    // Employee-specific responses (default)
    if (isEmployee) {
      if (lowerMessage.includes('claim') && currentPageTitle.includes('Claim')) {
        return `I can see you're working with claims. On this page, you can submit new claims, check the status of existing ones, and upload receipts for reimbursement. Would you like help with filing a claim or checking on one?`;
      }
      
      if (lowerMessage.includes('benefit') || lowerMessage.includes('coverage')) {
        return `I'd be happy to help you with benefits! Since you're on the ${currentPageTitle} page, I can provide specific guidance. What would you like to know about your benefits or coverage?`;
      }
      
      if (lowerMessage.includes('w-4') || lowerMessage.includes('tax') && currentPageTitle.includes('W-4')) {
        return `You're on the W-4 Tax Withholding Form page. This form determines how much federal income tax is withheld from your paycheck. I can help you understand allowances, exemptions, and how to fill it out correctly. What specific questions do you have?`;
      }
      
      if (lowerMessage.includes('family') || lowerMessage.includes('dependent')) {
        return `You can add family members as dependents for your benefit coverage. This includes spouses, children, and other eligible dependents. I can help you understand eligibility requirements and how to add them to your enrollment.`;
      }
    }
    
    // Role-appropriate security responses
    if (lowerMessage.includes('admin') || lowerMessage.includes('administrator')) {
      if (!isAdmin) {
        return `I can only assist you with features available to your role. If you need administrative assistance, please contact your system administrator or HR manager.`;
      }
    }
    
    if (lowerMessage.includes('hr') || lowerMessage.includes('company')) {
      if (isEmployee && !isHR && !isAdmin) {
        return `I can help with your personal benefits and enrollment. For company-wide questions or HR matters, please contact your HR department.`;
      }
    }
    
    // Generic response with page context and role-appropriate scope
    return `${aiIdentity} I can see you're on the **${currentPageTitle}** page. ${currentPageDescription ? currentPageDescription + '\n\n' : ''}${aiScope}`;
  };

  const sendMessage = async () => {
    if (!input.trim()) return;
    
    const userMessage = input.trim();
    setInput('');
    setLoading(true);

    try {
      if (aiConfig.provider === 'vapi') {
        // For VAPI - send with page context included in input array
        setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
        
        // Build input array with context
        const inputArray = [];
        
        // Add page context
        if (pageContext) {
          const contextMessage = generateAIContextMessage(pageContext, {
            itemCount: context.cartItems?.length || 0,
            selectedCategory: context.selectedCategory,
            enrollmentStatus: context.enrollmentStatus
          });
          
          inputArray.push({
            role: 'system',
            content: `[Page Context] ${contextMessage}`
          });
        }
        
        // Add user message
        inputArray.push({
          role: 'user',
          content: userMessage
        });
        
        console.log('Sending to VAPI with context:', inputArray);
        
        // Note: The VAPI widget will handle sending this via its chat API
        // For now, show a placeholder response
        setTimeout(() => {
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `I received your message "${userMessage}". (VAPI text chat requires API integration - context included: ${pageContext?.title})`
          }]);
        }, 500);
      } else {
        // Use built-in Host AI (Mock AI with context awareness)
        if (!conversationId) {
          await initConversation();
        }
        
        const conversation = await api.agents.getConversation(conversationId);
        
        // Add user message
        await api.agents.addMessage(conversation, {
          role: 'user',
          content: userMessage
        });
        
        // Generate and add AI response
        const aiResponse = generateMockAIResponse(userMessage, conversation);
        await api.agents.addMessage(conversationId, {
          role: 'assistant',
          content: aiResponse
        });
      }
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setLoading(false);
    }
  };

  const startVoiceCall = async () => {
    if (!vapiClient || !aiConfig.vapiAgentId) return;

    try {
      // Start the call
      await vapiClient.start(aiConfig.vapiAgentId);
      
      // Send initial page context to VAPI
      if (pageContext) {
        const contextMessage = generateAIContextMessage(pageContext, {
          itemCount: context.cartItems?.length || 0,
          selectedCategory: context.selectedCategory,
          enrollmentStatus: context.enrollmentStatus
        });
        
        // Send context as system message
        vapiClient.send({
          type: 'add-message',
          message: {
            role: 'system',
            content: `[Initial Context] ${contextMessage}\n\nThe user is now starting a voice conversation with you.`
          }
        });
      }
      
      setMessages([{ role: 'assistant', content: 'Voice call started. You can now speak!' }]);
    } catch (err) {
      console.error('Failed to start voice call:', err);
    }
  };

  const endVoiceCall = () => {
    if (vapiClient) {
      vapiClient.stop();
    }
  };

  const toggleMute = () => {
    if (vapiClient) {
      vapiClient.setMuted(!isMuted);
      setIsMuted(!isMuted);
    }
  };

  const startNewChatSession = () => {
    if (isCallActive && vapiClient) {
      vapiClient.stop();
    }
    clearSession();
    initConversation();
  };

  // Note: startNewChat function is available for future UI integration (e.g., "New Chat" button)
   
  const startNewChat = () => {
    openConfirm({
      title: 'Start a new chat session?',
      description: 'This ends the current conversation and clears the running context. Any active voice call will stop.',
      confirmLabel: 'Start new chat',
      onConfirm: startNewChatSession
    });
  };

  return (
    <>
      {/* Floating Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            className="fixed bottom-6 right-6 z-50"
          >
            <Button
              onClick={() => setIsOpen(true)}
              className="w-16 h-16 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 shadow-2xl relative"
              size="icon"
            >
              <MessageCircle className="w-7 h-7" />
              {/* Show badge if there's an active session */}
              {conversationId && messages.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 rounded-full border-2 border-white text-xs flex items-center justify-center text-white font-bold">
                  {messages.filter(m => m.role !== 'system').length}
                </span>
              )}
            </Button>
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className={`fixed z-50 ${
              isMinimized
                ? 'bottom-6 right-6 w-80'
                : 'bottom-6 right-6 w-[480px] h-[600px]'
            }`}
          >
            <Card className="h-full flex flex-col shadow-2xl border-2 border-purple-200">
              {/* Header */}
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-4 rounded-t-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                      <MessageCircle className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-semibold">Victoria AI</p>
                      <p className="text-xs opacity-90">
                        {(() => {
                          if (aiConfig.provider === 'vapi') return 'Voice & Chat';
                          const userRole = currentUser?.user_role || currentUser?.role || 'employee';
                          if (userRole === 'admin' || userRole === 'sub_admin') return 'Admin Assistant';
                          if (userRole === 'employer_hr') return 'HR Assistant';
                          return 'Benefits Advisor';
                        })()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {aiConfig.provider === 'vapi' && vapiClient && (
                      <>
                        {isCallActive ? (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={toggleMute}
                              className={`text-white hover:bg-white/20 ${isMuted ? 'bg-white/30' : ''}`}
                            >
                              {isMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={endVoiceCall}
                              className="text-white hover:bg-red-500/50 bg-red-500/30"
                            >
                              <Phone className="w-4 h-4" />
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={startVoiceCall}
                            className="text-white hover:bg-white/20"
                          >
                            <Phone className="w-4 h-4" />
                          </Button>
                        )}
                      </>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setIsMinimized(!isMinimized)}
                      className="text-white hover:bg-white/20"
                    >
                      {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setIsOpen(false);
                        // Keep session active when just closing the window
                      }}
                      className="text-white hover:bg-white/20"
                      title="Minimize chat"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Page Context Indicator */}
                {pageContext && conversationId && (
                  <div className="mt-2 text-xs opacity-80 bg-white/10 rounded px-2 py-1 flex items-center gap-1">
                    <span>📍</span>
                    <span>Currently on: {pageContext.title}</span>
                  </div>
                )}
              </div>

              {!isMinimized && (
                <>
                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
                    {messages.length === 0 && (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-4">
                          <MessageCircle className="w-8 h-8 text-white" />
                        </div>
                        {(() => {
                          const userRole = currentUser?.user_role || currentUser?.role || 'employee';
                          const isAdmin = userRole === 'admin' || userRole === 'sub_admin';
                          const isHR = userRole === 'employer_hr';
                          
                          if (isAdmin) {
                            return (
                              <>
                                <p className="text-slate-600 font-medium mb-2">Hi! I'm Victoria, your admin assistant.</p>
                                <p className="text-sm text-slate-500">
                                  I can help with system administration, employer management, benefit configuration, and more.
                                  {pageContext && (
                                    <>
                                      <br /><br />
                                      You're on <strong>{pageContext.title}</strong>. Ask me anything!
                                    </>
                                  )}
                                </p>
                              </>
                            );
                          } else if (isHR) {
                            return (
                              <>
                                <p className="text-slate-600 font-medium mb-2">Hi! I'm Victoria, your HR assistant.</p>
                                <p className="text-sm text-slate-500">
                                  I can help with employee management, company enrollments, reports, and HR tasks.
                                  {pageContext && (
                                    <>
                                      <br /><br />
                                      You're on <strong>{pageContext.title}</strong>. Ask me anything!
                                    </>
                                  )}
                                </p>
                              </>
                            );
                          } else {
                            return (
                              <>
                                <p className="text-slate-600 font-medium mb-2">Hi! I'm Victoria, your benefits assistant.</p>
                                {pageContext ? (
                                  <p className="text-sm text-slate-500">
                                    I can see you're on the <strong>{pageContext.title}</strong> page. 
                                    <br />Ask me anything!
                                  </p>
                                ) : (
                                  <p className="text-sm text-slate-500">Ask me anything about your benefits enrollment!</p>
                                )}
                              </>
                            );
                          }
                        })()}
                      </div>
                    )}
                    
                    {messages.map((message, idx) => {
                      // Check if this is a system message (page context update)
                      const isSystemMessage = message.role === 'system';
                      
                      if (isSystemMessage) {
                        // Render page context updates as small info badges
                        return (
                          <div key={idx} className="flex justify-center">
                            <div className="text-xs text-slate-500 bg-slate-100 rounded-full px-3 py-1 flex items-center gap-1">
                              <span>📍</span>
                              <span>{message.metadata?.pageTitle || 'Page changed'}</span>
                            </div>
                          </div>
                        );
                      }
                      
                      return (
                        <div
                          key={idx}
                          className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                              message.role === 'user'
                                ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                                : 'bg-white text-slate-800 border border-slate-200'
                            }`}
                          >
                            {message.role === 'user' ? (
                              <p className="text-sm">{message.content}</p>
                            ) : (
                              <ReactMarkdown className="text-sm prose prose-sm max-w-none">
                                {message.content}
                              </ReactMarkdown>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    
                    {loading && (
                      <div className="flex justify-start">
                        <div className="bg-white rounded-2xl px-4 py-3 border border-slate-200">
                          <Loader2 className="w-4 h-4 text-purple-500 animate-spin" />
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Input */}
                  <div className="p-4 bg-white border-t">
                    <div className="flex gap-2">
                      <Input
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                        placeholder="Ask about benefits, coverage, or pricing..."
                        className="flex-1"
                        disabled={loading}
                      />
                      <Button
                        onClick={sendMessage}
                        disabled={!input.trim() || loading}
                        className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
      <ConfirmDialog {...confirmDialogProps} />
    </>
  );
}